import { useMemo, useState } from 'react'
import { Box, Checkbox, InputAdornment, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { icons } from '../../../../../utils/icons'
import ConfigLayout from '../components/configLayout'
import { useStyles } from './configureItems.styles'
import { ConfigureItemsProps, PlanItem } from '../types'

const ConfigureItems = ({ items, onSave, onBack }: ConfigureItemsProps) => {
  const { t } = useTranslation()
  const styles = useStyles()

  const [localItems, setLocalItems] = useState<PlanItem[]>(items)
  const [searchTerm, setSearchTerm] = useState('')

  const handleToggleItem = (itemId: string) => {
    setLocalItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item

        return {
          ...item,
          selected: !item.selected,
          answer: item.selected ? '' : item.answer,
        }
      })
    )
  }

  const handleAnswerChange = (itemId: string, answer: string) => {
    setLocalItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, answer } : item
      )
    )
  }

  const handleSave = () => {
    onSave(localItems)
  }

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return localItems
    const term = searchTerm.toLowerCase()
    return localItems.filter((item) =>
      t(item.label).toLowerCase().includes(term)
    )
  }, [localItems, searchTerm, t])

  const selectedItems = useMemo(
    () => localItems.filter((item) => item.selected),
    [localItems]
  )

  const hasSelectedItems = selectedItems.length > 0
  const hasSelectedItemsWithoutAnswer = selectedItems.some((item) => !item.answer.trim())

  return (
    <ConfigLayout
      title={t('actionPlan.manualCreation.items.configureTitle')}
      subtitle={t('actionPlan.manualCreation.items.configureSubtitle')}
      onBack={onBack}
      onSave={handleSave}
      saveDisabled={!hasSelectedItems || hasSelectedItemsWithoutAnswer}
      saveLabel={t('actionPlan.manualCreation.items.saveAndBack')}
      backLabel={t('actionPlan.manualCreation.items.back')}
    >
      <Box sx={styles.content}>
        <Box sx={styles.leftPanel}>
          <Typography sx={styles.panelHeader}>{t('actionPlan.manualCreation.items.panelTitle')}</Typography>

          <TextField
            placeholder={t('actionPlan.manualCreation.items.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={styles.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  {icons('search')}
                </InputAdornment>
              ),
            }}
          />

          <Box sx={styles.itemsList}>
            {filteredItems.map((item) => {
              const label = t(item.label)
              return (
                <Box
                  key={item.id}
                  role='button'
                  aria-pressed={item.selected}
                  onClick={() => handleToggleItem(item.id)}
                  sx={styles.itemRow}
                >
                  <Checkbox
                    checked={item.selected}
                    onChange={() => handleToggleItem(item.id)}
                    onClick={(event) => event.stopPropagation()}
                    color='primary'
                    sx={{
                      padding: '2px',
                      '& .MuiBox-root': { width: '24px', height: '24px' }
                    }}
                  />
                  <Typography sx={styles.itemLabel}>{label}</Typography>
                </Box>
              )
            })}
          </Box>
        </Box>

        <Box sx={styles.rightPanel}>
          {!hasSelectedItems ? (
            <Typography sx={styles.emptyState}>
              {t('actionPlan.manualCreation.items.noItemSelected')}
            </Typography>
          ) : (
            <Box sx={styles.selectedItemsList}>
              {selectedItems.map((item) => (
                <Box key={item.id} sx={styles.selectedItemCard}>
                  <Typography sx={styles.selectedItemTitle}>{t(item.label)}</Typography>
                  <TextField
                    multiline
                    minRows={6}
                    maxRows={8}
                    value={item.answer}
                    label={t('actionPlan.manualCreation.items.answerPlaceholder')}
                    onChange={(e) => handleAnswerChange(item.id, e.target.value)}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ConfigLayout>
  )
}

export default ConfigureItems
